import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchJson } from '@/lib/api';
import { usePrecioCobertura, dinero } from '@/lib/precioCobertura';
import {
  AvisoMercaderia, AvisoPais, INPUT, Modal, Row, categoriaElegida, paisesCotizables, tipoDeCobertura,
} from './comunes';

/**
 * Emitir una cobertura suelta. Se cobra de la billetera (Carlos, 9-oct): el
 * monto va en el boton y sin precio no se emite.
 */
export default function ModalCobertura({ cfg, token, setup, onClose, onIssued }) {
  const [qTier, setQTier] = useState('A');
  const [qService, setQService] = useState('ground');
  const [qValue, setQValue] = useState('');
  const [qZip, setQZip] = useState('');
  const [qOverride, setQOverride] = useState('');
  const [qResidential, setQResidential] = useState(false);
  // Sin esto el backend salta la comprobacion de mercaderia prohibida
  // entera (`if payload.commodity_type:`), asi que la regla que prometen
  // los terminos no se ejecutaba nunca desde esta pantalla.
  const [qCommodity, setQCommodity] = useState('');
  // El tope de viernes-para-lunes esta en config y en los terminos, y no
  // habia forma de activarlo desde ninguna pantalla.
  const [qFriday, setQFriday] = useState(false);
  const [qCarrier, setQCarrier] = useState('');
  // Sin el tipo de paquete el backend no puede aplicar la regla de los sobres
  // overnight (regla 7 del documento de PACKIFI).
  const [qPackage, setQPackage] = useState('box');
  // Pais de destino. La cotizacion solo tenia ZIP, que es de Estados Unidos,
  // asi que ningun limite por pais del documento de PACKIFI se podia aplicar.
  const [qCountry, setQCountry] = useState('US');
  // Categorias que dicto Mike el 4-oct. Arrancan en blanco a proposito: el
  // comerciante tiene que elegir, no aceptar una que venia puesta.
  const [qCategory, setQCategory] = useState('');
  const [qSubtype, setQSubtype] = useState('');
  const [qFragile, setQFragile] = useState(false);
  const [qBusy, setQBusy] = useState(false);
  const [qError, setQError] = useState('');

  const { precio: precioQ, error: errorPrecioQ } = usePrecioCobertura({
    token, tier: qTier, declared: qValue, category: qCategory, enabled: true,
  });

  const submitQuote = async () => {
    setQBusy(true); setQError('');
    try {
      const d = await fetchJson('/insurance/selletrans/quote', {
        token, method: 'POST',
        body: {
          coverage_tier: qTier,
          service_level: qService,
          declared_value: parseFloat(qValue),
          destination_zip: qZip,
          is_residential: qResidential,
          friday_for_monday: qFriday,
          commodity_type: qCommodity.trim(),
          commodity_category: qCategory,
          commodity_subtype: qSubtype || undefined,
          is_fragile: qFragile,
          carrier: qCarrier || undefined,
          package_type: qPackage,
          // Los comerciantes despachan desde Estados Unidos.
          origin_country: 'US',
          destination_country: qCountry,
          override_code: qOverride || undefined,
        },
      });
      onIssued(`Coverage ${d.verification_number} issued · ${dinero(d.merchant_charge?.amount)} charged to your wallet`);
    } catch (e) {
      // El rechazo se dibuja aca adentro: en la franja de la pagina quedaba
      // detras de esta ventana y el comerciante no veia que tenia que llamar.
      setQError(e.message);
    } finally {
      setQBusy(false);
    }
  };

  const conSubtipos = categoriaElegida(cfg, qCategory)?.subtypes?.length > 0;

  return (
    <Modal onClose={onClose} title="Add coverage" testid="quote-modal">
      <div className="space-y-3">
        <Row label="Coverage tier">
          <div className="grid grid-cols-4 gap-2">
            {cfg.tiers.map(t => (
              <button key={t.code} type="button" onClick={() => setQTier(t.code)}
                className={`p-2 rounded-lg border-2 text-xs ${qTier === t.code ? 'border-purple-500 bg-purple-50' : 'border-slate-200'}`}
                data-testid={`q-tier-${t.code}`}>
                <b>{t.code}</b><br /><span className="text-slate-500">{tipoDeCobertura(t)}</span>
              </button>
            ))}
          </div>
        </Row>
        <Row label="Service level">
          <select className={INPUT} value={qService} onChange={e => setQService(e.target.value)} data-testid="q-service">
            {Object.keys(cfg.service_level_defaults).map(s => <option key={s} value={s}>{s.replaceAll('_', ' ')} · max ${cfg.service_level_defaults[s].toLocaleString()}</option>)}
          </select>
        </Row>
        <Row label="Declared value ($)">
          <input type="number" min={1} className={INPUT} value={qValue} onChange={e => setQValue(e.target.value)} data-testid="q-value" />
        </Row>
        <Row label="Destination country">
          <select className={INPUT} value={qCountry} onChange={e => setQCountry(e.target.value)} data-testid="q-country">
            {paisesCotizables(cfg).map(([codigo, nombre]) => <option key={codigo} value={codigo}>{nombre}</option>)}
          </select>
          <AvisoPais cfg={cfg} pais={qCountry} carrier={qCarrier} />
        </Row>
        <Row label="Destination ZIP">
          <input className={INPUT} value={qZip} onChange={e => setQZip(e.target.value)} placeholder="98101" data-testid="q-zip" />
          {cfg.cap_zips.includes(qZip) && <p className="text-xs text-amber-600 mt-1">⚠ This ZIP is capped at ${cfg.cap_zip_max_value.toLocaleString()}.</p>}
        </Row>
        <Row label="What kind of goods">
          <select className={INPUT} value={qCategory}
            onChange={e => { setQCategory(e.target.value); setQSubtype(''); }} data-testid="q-category">
            <option value="">Choose a category…</option>
            {(cfg.commodity_categories || []).map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
          </select>
          {conSubtipos && (
            <select className={`${INPUT} mt-2`} value={qSubtype}
              onChange={e => setQSubtype(e.target.value)} data-testid="q-subtype">
              <option value="">Which kind?</option>
              {categoriaElegida(cfg, qCategory).subtypes.map(s => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
          )}
          <label className="flex items-start gap-2 text-sm mt-2 cursor-pointer">
            <input type="checkbox" className="mt-1" checked={qFragile} onChange={e => setQFragile(e.target.checked)} data-testid="q-fragile" />
            <span>Fragile or glass</span>
          </label>
          <AvisoMercaderia cfg={cfg} setup={setup} categoria={qCategory} subtipo={qSubtype} fragil={qFragile}
            pais={qCountry} codigo={qOverride} />
        </Row>
        <Row label="What is inside">
          <input
            className={INPUT}
            placeholder="e.g. Rolex Submariner, vintage records, auto parts"
            value={qCommodity}
            onChange={e => setQCommodity(e.target.value)}
            data-testid="q-commodity" />
          <p className="text-xs text-slate-500 mt-1">
            Required. Describe the item; it goes on the coverage record.
          </p>
          {/* Misma deteccion que el backend: la palabra entera, para que
              "bitcoin" no la active. Los montos salen de la config. */}
          {cfg.coin_max_fedex_ups && /\bcoins?\b/i.test(qCommodity) && (
            <p className="text-xs text-amber-600 mt-1">⚠ Collector coins are capped at ${cfg.coin_max_fedex_ups.toLocaleString()} by FedEx or UPS, and ${cfg.coin_max_other.toLocaleString()} by any other carrier.</p>
          )}
        </Row>
        <Row label="Package">
          <select className={INPUT} value={qPackage} onChange={e => setQPackage(e.target.value)} data-testid="q-package">
            {(cfg.package_types || ['box']).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {(cfg.uninsurable_package_types || []).includes(qPackage)
            && (cfg.overnight_service_levels || []).includes(qService) && (
            <p className="text-xs text-amber-600 mt-1">⚠ Letters and envelopes sent overnight are not covered.</p>
          )}
        </Row>
        <Row label="Carrier (optional)">
          <input className={INPUT} value={qCarrier} onChange={e => setQCarrier(e.target.value)} placeholder="FedEx, UPS, USPS" data-testid="q-carrier" />
          {/* USPS topa muy por debajo del resto, y el comerciante tiene
              que verlo ANTES de declarar el valor, no al recibir el error. */}
          {cfg.usps_domestic_max && /usps|postal/i.test(qCarrier) && (
            <p className="text-xs text-amber-600 mt-1">⚠ USPS is capped at ${cfg.usps_domestic_max.toLocaleString()} for domestic shipments, and international USPS is not covered.</p>
          )}
        </Row>
        <Row label="Delivery">
          <label className="flex items-start gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="mt-1" checked={qResidential} onChange={e => setQResidential(e.target.checked)} data-testid="q-residential" />
            <span>Residential address</span>
          </label>
          {qResidential && cfg.residential_max && (
            <p className="text-xs text-amber-600 mt-1">⚠ Residential deliveries are capped at ${cfg.residential_max.toLocaleString()}, whatever the service level.</p>
          )}
          <label className="flex items-start gap-2 text-sm mt-2">
            <input type="checkbox" className="mt-1" checked={qFriday} onChange={e => setQFriday(e.target.checked)} data-testid="q-friday" />
            <span>Shipped Friday for Monday delivery</span>
          </label>
          {qFriday && cfg.friday_for_monday_max && (
            <p className="text-xs text-amber-600 mt-1">⚠ A Friday shipment for Monday delivery is capped at ${cfg.friday_for_monday_max.toLocaleString()}. It spends two nights in the carrier's network.</p>
          )}
        </Row>
        <Row label="Office approval code (if the office gave you one)">
          <input className={`${INPUT} font-mono`} value={qOverride} onChange={e => setQOverride(e.target.value.toUpperCase())} data-testid="q-override" />
        </Row>
        {qError && (
          <p className="text-sm text-red-600 bg-red-50 dark:bg-red-950/30 rounded-lg p-3" data-testid="q-error">{qError}</p>
        )}
        {errorPrecioQ && (
          <p className="text-sm text-red-600" data-testid="q-price-error">{errorPrecioQ}</p>
        )}
        {/* Issuing charges the wallet: without a price on the button it does not issue. */}
        <Button onClick={submitQuote} disabled={qBusy || !precioQ || !qValue || !qZip || !qCommodity.trim() || !qCategory
          || (conSubtipos && !qSubtype)} className="w-full bg-gradient-to-r from-purple-500 to-indigo-500" data-testid="q-submit">
          {qBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : precioQ ? `Issue · ${dinero(precioQ.amount)} charged to your wallet` : 'Getting the price…'}
        </Button>
      </div>
    </Modal>
  );
}
